import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SecurityContext} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {SingleEditEnforcer, SingleEditTrackable} from '@webmate-web-core/shared/components/forms-commons/single-edit-enforcer.service';
import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { javascript } from '@codemirror/lang-javascript';
import {DomSanitizer} from '@angular/platform-browser';

export type CodeEditMode = 'html' | 'css' | 'javascript';

export type EditMode = 'visual' | CodeEditMode;
export type EditableTextToolbarPreset = 'default' | 'basicRichText';

@Component({
    selector: 'wm-editable-text',
    templateUrl: './editable-text.component.html',
    styleUrls: ['./editable-text.component.less'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: EditableTextComponent
        }
    ]
})
/**
 * Display text that can be edited in an editor on click.
 * (Like in Jira UI)
 */
export class EditableTextComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, SingleEditTrackable {
    @Input() placeholder: string = '';

    @Input() supportedEditModes?: EditMode[]; // default is visual

    @Input() toolbarPreset: EditableTextToolbarPreset = 'default';

    @Input() visualEditorHeight: string = '250px';

    @Input() needsSave: boolean = true;

    @Input() readonly: boolean = false;

    @Input() content?: string;

    @Output() onTextChange: EventEmitter<string> = new EventEmitter<string>();

    originalContent: string;

    onChange = (content: string) => {}; // nop to prevent errors before init

    onTouched = () => {};

    touched = false;

    disabled = false;

    isEditing = false;

    editMode: EditMode;

    htmlExtensions = [html()];

    cssExtensions = [css()];

    javascriptExtensions = [javascript()];

    constructor(private singleEditEnforcer: SingleEditEnforcer, private readonly sanitizer: DomSanitizer) {
        singleEditEnforcer.registerTrackable(this);
    }

    ngOnChanges() {
        this.originalContent = this.content;
        if (this.isContentEmpty()) {
            this.content = "";
        }
    }

    ngOnInit() {
        this.editMode = this.supportedEditModes && this.supportedEditModes.length > 0 ? this.supportedEditModes[0] : 'visual';
    }

    ngOnDestroy() {
        this.singleEditEnforcer.unregisterTrackable(this);
    }

    isContentEmpty(): boolean {
        const html = this.sanitizer.sanitize(SecurityContext.HTML, this.content) || '';
        if (!html) return true;

        const temp = document.createElement('div');
        temp.innerHTML = html;

        return temp.textContent?.trim() === '';
    }

    setEditMode(mode: EditMode) {
        this.content = this.cleanHtml(this.content);
        this.editMode = mode;
    }

    disableEdit() {
        this.isEditing = false;
    }

    markAsTouched() {
        if (!this.touched) {
            this.onTouched();
            this.touched = true;
        }
    }

    save() {
        this.isEditing = false;
        this.onChange(this.content);
        this.onTextChange.emit(this.content);
        this.originalContent = this.content;
    }

    cancel() {
        this.isEditing = false;
        this.content = this.originalContent;
    }

    enableEditing() {
        if (this.readonly) {
            return;
        }
        this.content = this.cleanHtml(this.content);

        // we only want one instance to be editing at a time
        this.singleEditEnforcer.disableAllExcept(this);
        this.isEditing = true;
    }

    disableEditing() {
        this.isEditing = false;
    }

    registerOnChange(onChange: any): void {
        this.onChange = onChange;
    }

    registerOnTouched(onTouched: any): void {
        this.onTouched = onTouched;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = isDisabled;
    }

    writeValue(content: string): void {
        this.content = content;
        this.originalContent = content;
    }

    cleanHtml(content: string): string {
        if (!!content) {
            content = content.replace(/&nbsp;/g, ' ');
            return content;
        }
    }

    onEditorChange() {
        if (!this.needsSave) {
            setTimeout(() => {
                if (this.onChange) {
                    this.onChange(this.content);
                    this.onTextChange.emit(this.content);
                }
                if (this.onTouched) {
                    this.onTouched();
                }
            });
        }
    }

    get showModeSwitcher(): boolean {
        return !!this.supportedEditModes && this.supportedEditModes.length > 1;
    }

    get useCustomToolbarHeader(): boolean {
        return this.toolbarPreset === 'basicRichText';
    }

    get editorFormats(): string[] | undefined {
        if (this.toolbarPreset === 'basicRichText') {
            return ['bold', 'italic', 'list'];
        }

        return undefined;
    }

}
